import React, { useState, useMemo } from "react";
import { FileText, CheckCircle2, Receipt, Clock, ChevronDown, ChevronRight, Trash2 } from "lucide-react";

const getStatusBadgeInfo = (status = "") => {
  const s = status.toLowerCase();
  if (s.includes("grant") || s.includes("approved")) {
    return {
      label: "Approved",
      bg: "bg-emerald-50 text-emerald-700 border-emerald-200",
      dot: "bg-emerald-500"
    };
  }
  if (s.includes("refuse") || s.includes("reject")) {
    return {
      label: "Refused",
      bg: "bg-rose-50 text-rose-700 border-rose-200",
      dot: "bg-rose-500"
    };
  }
  return {
    label: "Pending",
    bg: "bg-amber-50 text-amber-700 border-amber-200",
    dot: "bg-amber-500"
  };
};

export default function FundingStatusTile({
  planner,
  setAidField,
  setFundingField,
  setApplicationField,
  removeUploadedLetter,
  clearAllUploadedLetters,
}) {
  const [expandedGroups, setExpandedGroups] = useState({
    applications: true,
    outcome: true,
    claims: true,
  });

  const toggleGroup = (groupName) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  const groupedLetters = useMemo(() => {
    const list = Array.isArray(planner.uploadedLetters) ? [...planner.uploadedLetters] : [];
    list.sort((a, b) => {
      const dateA = a.isoDate || a.uploadedAt || "";
      const dateB = b.isoDate || b.uploadedAt || "";
      return dateA.localeCompare(dateB);
    });

    const applications = [];
    const outcome = [];
    const claims = [];

    list.forEach((letter) => {
      const desc = (letter.description || "").toLowerCase();
      if (desc.includes("approved") || desc.includes("refused") || desc.includes("caveat") || desc.includes("contribution") || desc.includes("outcome")) {
        outcome.push(letter);
      } else if (desc.includes("claim") || desc.includes("prep") || desc.includes("invoice") || desc.includes("billing")) {
        claims.push(letter);
      } else {
        applications.push(letter);
      }
    });

    return { applications, outcome, claims };
  }, [planner.uploadedLetters]);

  const renderLetterTag = (letter) => {
    let IconComponent = Clock;
    let badgeClass = "bg-slate-100 text-slate-800 border-slate-200";
    let iconColor = "text-slate-500";
    
    const desc = (letter.description || "").toLowerCase();
    if (desc.includes("approved") || desc.includes("refused") || desc.includes("caveat") || desc.includes("contribution") || desc.includes("outcome")) {
      IconComponent = CheckCircle2;
      badgeClass = "bg-emerald-50/60 text-emerald-800 border-emerald-200/80";
      iconColor = "text-emerald-600";
    } else if (desc.includes("applied") || desc.includes("application") || desc.includes("worksheet") || desc.includes("report")) {
      IconComponent = FileText;
      badgeClass = "bg-purple-50/60 text-purple-800 border-purple-200/80";
      iconColor = "text-purple-600";
    } else if (desc.includes("claim") || desc.includes("prep") || desc.includes("invoice") || desc.includes("billing")) {
      IconComponent = Receipt;
      badgeClass = "bg-blue-50/60 text-blue-800 border-blue-200/80";
      iconColor = "text-blue-600";
    }

    return (
      <div key={letter.id} className="flex flex-col gap-1.5">
        <div
          className={`flex items-center justify-between p-2 rounded-lg border text-xs font-semibold ${badgeClass}`}
          title={letter.fileName}
        >
          <div className="flex items-center gap-2 min-w-0">
            <IconComponent className={`w-3.5 h-3.5 flex-shrink-0 ${iconColor}`} />
            {letter.fileData ? (
              <a
                href={letter.fileData}
                download={letter.fileName}
                className="hover:underline cursor-pointer font-bold truncate max-w-[140px] md:max-w-none text-inherit"
                title={`Download ${letter.fileName}`}
              >
                {letter.description}
              </a>
            ) : (
              <span className="truncate max-w-[140px] md:max-w-none">{letter.description}</span>
            )}
            {letter.date && !desc.includes("claim") && !desc.includes("billing") && !desc.includes("invoice") && (
              <span className="opacity-60 font-normal font-sans text-[10px] whitespace-nowrap">
                {(desc.includes("approved") || desc.includes("extension")) ? `Effective from ${letter.date}` : `Applied ${letter.date}`}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => removeUploadedLetter(letter.id)}
            className="ml-2 w-5 h-5 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-200/50 hover:text-rose-600 transition-colors text-xs"
            title={`Remove ${letter.fileName}`}
          >
            ×
          </button>
        </div>
        {letter.missingApprovalWarning && (
          <div className="flex items-start gap-2 px-2.5 py-2 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-800 leading-snug">
            <span className="flex-shrink-0 mt-px">⚠️</span>
            <span>
              This claim is for <strong>Extension {letter.claimExtensionNumber}</strong> but no matching approval letter has been uploaded yet.
              Please <strong>remove this claim</strong>, upload the <strong>Extension {letter.claimExtensionNumber} approval letter</strong> first, then re-upload this claim.
            </span>
          </div>
        )}
      </div>
    );
  };
  const formatFriendlyDate = (dateStr) => {
    if (!dateStr) return "";
    const parts = dateStr.split("-");
    if (parts.length === 3) {
      const [year, month, day] = parts;
      return `${day}/${month}/${year}`;
    }
    const slashParts = dateStr.split("/");
    if (slashParts.length === 3) {
      return dateStr;
    }
    return dateStr;
  };

  const extensions = useMemo(() => {
    if (Array.isArray(planner.aid.extensions) && planner.aid.extensions.length > 0) {
      return planner.aid.extensions;
    }
    return [
      {
        extensionNumber: planner.aid.extensionNumber || "0",
        aidType: planner.aid.aidType || "",
        aidTypeList: planner.aid.aidTypeList || [],
        dateApplied: planner.application.dateApplied || "",
        effectiveDate: planner.aid.effectiveDate || "",
        applicationStatus: planner.aid.applicationStatus || "Aid submitted - application pending",
      }
    ];
  }, [planner.aid, planner.application.dateApplied]);

  const hasAnyExtensions = useMemo(() => {
    return extensions.some(ext => {
      const num = parseInt(ext.extensionNumber);
      return !isNaN(num) && num > 0;
    });
  }, [extensions]);

  const updateExtensionField = (index, field, value) => {
    const nextExtensions = [...extensions];
    nextExtensions[index] = {
      ...nextExtensions[index],
      [field]: value
    };
    
    if (field === "aidType") {
      nextExtensions[index].aidTypeList = value
        .split(/;|,|\band\b/i)
        .map((item) => item.trim())
        .filter(Boolean);
    }
    
    setAidField("extensions", nextExtensions);
    
    const sorted = [...nextExtensions].sort((a, b) => {
      const numA = parseInt(a.extensionNumber) || 0;
      const numB = parseInt(b.extensionNumber) || 0;
      return numA - numB;
    });
    const highest = sorted[sorted.length - 1];
    if (highest) {
      setAidField("extensionNumber", highest.extensionNumber);
      setAidField("aidType", highest.aidType);
      setAidField("aidTypeList", highest.aidTypeList);
      setAidField("effectiveDate", highest.effectiveDate);
      setAidField("applicationStatus", highest.applicationStatus);
      setApplicationField("dateApplied", highest.dateApplied);
    }
  };

  const addNewExtensionUI = () => {
    const nextNum = extensions.reduce((max, ext) => {
      const num = parseInt(ext.extensionNumber) || 0;
      return num > max ? num : max;
    }, -1) + 1;
    
    const newExt = {
      extensionNumber: String(nextNum),
      aidType: "",
      aidTypeList: [],
      dateApplied: "",
      effectiveDate: "",
      applicationStatus: "Aid submitted - application pending",
    };
    
    setAidField("extensions", [...extensions, newExt]);
  };

  const deleteExtensionUI = (index) => {
    const nextExtensions = extensions.filter((_, idx) => idx !== index);
    setAidField("extensions", nextExtensions);
    
    const sorted = [...nextExtensions].sort((a, b) => {
      const numA = parseInt(a.extensionNumber) || 0;
      const numB = parseInt(b.extensionNumber) || 0;
      return numA - numB;
    });
    const highest = sorted[sorted.length - 1];
    if (highest) {
      setAidField("extensionNumber", highest.extensionNumber);
      setAidField("aidType", highest.aidType);
      setAidField("aidTypeList", highest.aidTypeList);
      setAidField("effectiveDate", highest.effectiveDate);
      setAidField("applicationStatus", highest.applicationStatus);
      setApplicationField("dateApplied", highest.dateApplied);
    }
  };

  const handleRemoveAllHistory = () => {
    clearAllUploadedLetters();
  };

  return (
    <section className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden border-t-4 border-t-purple-600 flex flex-col h-full font-sans">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-800">Funding Information</h2>
        <div className="text-slate-300">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
          </svg>
        </div>
      </div>
      <div className="p-5 flex-1 space-y-5">
        <div className="grid grid-cols-1 gap-4">
          
          {/* Aid Number first box */}
          <div className="bg-slate-50 border border-slate-200/60 rounded-xl p-4 space-y-3">
            <div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Aid Number</span>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <input
                  value={planner.aid.aidNumber || ""}
                  onChange={(e) => setAidField("aidNumber", e.target.value)}
                  className="text-sm font-bold text-slate-800 bg-transparent border-b border-dashed border-slate-300 focus:border-purple-500 focus:outline-none w-32"
                  placeholder="e.g. 25A834943"
                />
                {planner.aid.applicationStatus && (() => {
                  const badge = getStatusBadgeInfo(planner.aid.applicationStatus);
                  return (
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${badge.bg}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${badge.dot}`} />
                      {badge.label}
                    </span>
                  );
                })()}
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-purple-50 text-purple-700 border border-purple-100">
                  <span className="text-[11px] font-bold text-purple-400 uppercase tracking-wide whitespace-nowrap">Guideline:</span>
                  <input
                    value={planner.aid.guideline || ""}
                    onChange={(e) => setAidField("guideline", e.target.value)}
                    className="bg-transparent border-none text-purple-800 font-bold outline-none text-[11px] focus:ring-0 focus:outline-none w-24 p-0 placeholder:text-purple-300"
                    placeholder="e.g. GL 1"
                  />
                </span>
              </div>
            </div>

            <div className="pt-2 border-t border-slate-200/60 grid grid-cols-2 gap-3">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">Applied on</span>
                <input
                  type="date"
                  value={planner.application.dateApplied || ""}
                  onChange={(e) => setApplicationField("dateApplied", e.target.value)}
                  className="text-xs font-semibold text-slate-700 bg-transparent border-b border-dashed border-slate-300 focus:border-purple-500 focus:outline-none mt-1 w-full"
                />
              </div>
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">Effective Date</span>
                <input
                  type="date"
                  value={planner.aid.effectiveDate || ""}
                  onChange={(e) => setAidField("effectiveDate", e.target.value)}
                  className="text-xs font-semibold text-slate-700 bg-transparent border-b border-dashed border-slate-300 focus:border-purple-500 focus:outline-none mt-1 w-full"
                />
              </div>
            </div>

            {/* List of professional costs for initial request */}
            {planner.aid.aidTypeList && planner.aid.aidTypeList.length > 0 && (
              <div className="pt-2.5 border-t border-slate-200/60">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1.5">Aid Types Applied For</span>
                <div className="flex flex-wrap gap-1.5">
                  {planner.aid.aidTypeList.map((cost, cIdx) => (
                    <span
                      key={`init-cost-${cIdx}`}
                      className="inline-flex items-center px-2.5 py-1 rounded-md text-[11px] font-bold bg-purple-50 text-purple-700 border border-purple-100"
                    >
                      {cost}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Extensions List */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Extensions & Variations</span>
              <button
                type="button"
                onClick={addNewExtensionUI}
                className="text-xs text-purple-600 hover:text-purple-800 font-bold transition-colors"
              >
                + Add Extension
              </button>
            </div>
            
            {hasAnyExtensions && (
              <div className="space-y-3">
                {extensions
                  .map((ext, idx) => ({ ext, idx }))
                  .filter(({ ext }) => {
                    const num = parseInt(ext.extensionNumber);
                    return !isNaN(num) && num > 0;
                  })
                  .map(({ ext, idx }) => (
                    <div key={`ext-card-${idx}`} className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow relative">
                      
                      {/* Header row: Ext Number */}
                      <div className="flex items-center justify-between gap-4 mb-3">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-slate-400">Ext</span>
                          <input
                            value={ext.extensionNumber}
                            onChange={(e) => updateExtensionField(idx, "extensionNumber", e.target.value)}
                            className="w-10 h-7 border border-slate-200 rounded text-center text-xs font-bold focus:border-purple-500 outline-none"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => deleteExtensionUI(idx)}
                          className="text-slate-400 hover:text-rose-500 transition-colors p-1"
                          title="Delete Extension"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Status row: full width */}
                      <div className="space-y-1.5 mb-3">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Status</span>
                        {(() => {
                          const badge = getStatusBadgeInfo(ext.applicationStatus || "");
                          return (
                            <div className="flex items-center gap-2">
                              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold border ${badge.bg}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${badge.dot}`} />
                                {badge.label}
                              </span>
                            </div>
                          );
                        })()}
                        <input
                          value={ext.applicationStatus}
                          onChange={(e) => updateExtensionField(idx, "applicationStatus", e.target.value)}
                          className="w-full h-8 border border-slate-200 rounded-lg px-2.5 text-xs focus:border-purple-500 outline-none"
                          placeholder="e.g. Aid submitted - application pending"
                        />
                      </div>

                      {/* Aid Type row: list of professional costs */}
                      <div className="space-y-1.5 mb-3">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Aid Type / Professional Costs</span>
                        <input
                          value={ext.aidType}
                          onChange={(e) => updateExtensionField(idx, "aidType", e.target.value)}
                          className="w-full h-8 border border-slate-200 rounded-lg px-2.5 text-xs focus:border-purple-500 outline-none"
                          placeholder="Bail, SUMMARY HEARING-LUMP SUM, psychological new assessment..."
                        />
                      </div>

                      {/* Dates row: date applied for and effective date */}
                      <div className="grid grid-cols-2 gap-3">
                        <label className="space-y-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                          <span>Date Applied For</span>
                          <input
                            type="date"
                            value={ext.dateApplied || ""}
                            onChange={(e) => updateExtensionField(idx, "dateApplied", e.target.value)}
                            className="w-full h-8 border border-slate-200 rounded-lg px-2 text-xs focus:border-purple-500 outline-none font-sans mt-1"
                          />
                        </label>
                        <label className="space-y-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                          <span>Effective Date</span>
                          <input
                            type="date"
                            value={ext.effectiveDate || ""}
                            onChange={(e) => updateExtensionField(idx, "effectiveDate", e.target.value)}
                            className="w-full h-8 border border-slate-200 rounded-lg px-2 text-xs focus:border-purple-500 outline-none font-sans mt-1"
                          />
                        </label>
                      </div>
                      {/* Delete extension button */}
                      {extensions.length > 1 && (
                        <button
                          type="button"
                          onClick={() => deleteExtensionUI(idx)}
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-slate-100 hover:bg-rose-100 text-slate-400 hover:text-rose-600 flex items-center justify-center text-xs shadow-sm transition-colors"
                          title="Delete Extension"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>

        {/* Compact Document Upload History Row */}
        {planner.uploadedLetters && planner.uploadedLetters.length > 0 && (
          <div className="bg-slate-50/60 border border-slate-100 rounded-xl p-3">
            <div className="flex items-center justify-between gap-4 mb-2">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Letter Upload History</span>
              <button
                type="button"
                onClick={handleRemoveAllHistory}
                className="text-[10px] font-bold uppercase tracking-wider text-rose-600 hover:text-rose-800 transition-colors"
              >
                Clear All
              </button>
            </div>
            
            <div className="space-y-2 mt-2">
              {/* Applications Group */}
              {groupedLetters.applications.length > 0 && (
                <div className="border border-slate-200/60 rounded-lg bg-white overflow-hidden shadow-sm">
                  <button
                    type="button"
                    onClick={() => toggleGroup("applications")}
                    className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-slate-700 bg-slate-50/50 hover:bg-slate-50 transition-colors"
                  >
                    <span>Applications ({groupedLetters.applications.length})</span>
                    {expandedGroups.applications ? (
                      <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                    ) : (
                      <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                    )}
                  </button>
                  {expandedGroups.applications && (
                    <div className="p-2 flex flex-col gap-1.5 border-t border-slate-100 bg-slate-50/20">
                      {groupedLetters.applications.map((letter) => renderLetterTag(letter))}
                    </div>
                  )}
                </div>
              )}

              {/* Outcome Group */}
              {groupedLetters.outcome.length > 0 && (
                <div className="border border-slate-200/60 rounded-lg bg-white overflow-hidden shadow-sm">
                  <button
                    type="button"
                    onClick={() => toggleGroup("outcome")}
                    className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-slate-700 bg-slate-50/50 hover:bg-slate-50 transition-colors"
                  >
                    <span>Outcome ({groupedLetters.outcome.length})</span>
                    {expandedGroups.outcome ? (
                      <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                    ) : (
                      <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                    )}
                  </button>
                  {expandedGroups.outcome && (
                    <div className="p-2 flex flex-col gap-1.5 border-t border-slate-100 bg-slate-50/20">
                      {groupedLetters.outcome.map((letter) => renderLetterTag(letter))}
                    </div>
                  )}
                </div>
              )}

              {/* Claims Group */}
              {groupedLetters.claims.length > 0 && (
                <div className="border border-slate-200/60 rounded-lg bg-white overflow-hidden shadow-sm">
                  <button
                    type="button"
                    onClick={() => toggleGroup("claims")}
                    className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-slate-700 bg-slate-50/50 hover:bg-slate-50 transition-colors"
                  >
                    <span>Claims ({groupedLetters.claims.length})</span>
                    {expandedGroups.claims ? (
                      <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                    ) : (
                      <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
                    )}
                  </button>
                  {expandedGroups.claims && (
                    <div className="p-2 flex flex-col gap-1.5 border-t border-slate-100 bg-slate-50/20">
                      {groupedLetters.claims.map((letter) => renderLetterTag(letter))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
